import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, FormControl, InputLabel, Select, MenuItem, Box,
  Typography, Divider, Alert, Autocomplete, Chip, IconButton,
  CircularProgress, Collapse, Tooltip,
  Checkbox, FormControlLabel, Stack,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ExpandMore as ExpandMoreIcon, ExpandLess as CollapseIcon, ExpandMore as ExpandIcon, NotificationsActive as SendReminderIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import careerEventsService from '../../services/careerEventsService';
import apiService from '../../services/apiService';

export default function CareerEventDialog({ open, onClose, event, employeeId, personName, eventTypes, viewMode }) {
  const { t } = useTranslation();
  const isEdit = !!event?.id;

  const statusOptions = [
    { value: 'Scheduled', label: t('careerEvents.status.scheduled', 'Scheduled') },
    { value: 'InProgress', label: t('careerEvents.status.inProgress', 'In Progress') },
    { value: 'Completed', label: t('careerEvents.status.completed', 'Completed') },
    { value: 'Cancelled', label: t('careerEvents.status.cancelled', 'Cancelled') }
  ];

  const [form, setForm] = useState({
    eventTypeId: '',
    eventDate: new Date(),
    title: '',
    description: '',
    outcome: '',
    status: 'Scheduled',
    visibleToEmployee: false
  });
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [newFollowUp, setNewFollowUp] = useState({
    description: '',
    dueDate: null,
    assignedToUserId: null,
    additionalRecipientUserIds: [],
    reminderStartDaysBefore: 14,
    reminderFrequencyDays: 7,
    status: 'Pending'
  });

  const followUpStatusOptions = [
    { value: 'Pending', label: t('careerEvents.followUp.statusValues.pending', 'Pending') },
    { value: 'InProgress', label: t('careerEvents.followUp.statusValues.inprogress', 'In Progress') },
    { value: 'Completed', label: t('careerEvents.followUp.statusValues.completed', 'Completed') },
    { value: 'Cancelled', label: t('careerEvents.followUp.statusValues.cancelled', 'Cancelled') }
  ];
  const [existingFollowUps, setExistingFollowUps] = useState([]);
  const [followUpsExpanded, setFollowUpsExpanded] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingFollowUp, setAddingFollowUp] = useState(false);
  const [editingFollowUpId, setEditingFollowUpId] = useState(null); // ID of follow-up being edited
  const [expandedFollowUpId, setExpandedFollowUpId] = useState(null); // ID of expanded accordion item
  const [deletingId, setDeletingId] = useState(null);
  const [sendingReminderId, setSendingReminderId] = useState(null);
  const [error, setError] = useState(null);

  // Send Reminder Dialog state
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderFollowUp, setReminderFollowUp] = useState(null);
  const [reminderRecipients, setReminderRecipients] = useState([]);
  const [reminderUpdateTracking, setReminderUpdateTracking] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  // User search for assignee
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAdditionalUsers, setSelectedAdditionalUsers] = useState([]);

  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) return;
    setUsersLoading(true);
    try {
      const response = await apiService.api.get(`/users/search?query=${encodeURIComponent(query)}&limit=20`);
      setUsers(response.data || response || []);
    } catch (err) {
      console.error('Failed to search users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    const loadEventDetails = async () => {
      if (event?.id && open) {
        // Always fetch full details to get follow-ups
        setLoadingDetails(true);
        try {
          const fullEvent = await careerEventsService.getEvent(event.id);
          setForm({
            eventTypeId: fullEvent.eventTypeId || '',
            eventDate: fullEvent.eventDate ? new Date(fullEvent.eventDate) : new Date(),
            title: fullEvent.title || '',
            description: fullEvent.description || '',
            outcome: fullEvent.outcome || '',
            status: fullEvent.status || 'Scheduled',
            visibleToEmployee: fullEvent.visibleToEmployee || false
          });
          setExistingFollowUps(fullEvent.followUps || []);
        } catch (err) {
          console.error('Failed to load event details:', err);
          // Fallback to passed event data
          setForm({
            eventTypeId: event.eventTypeId || '',
            eventDate: event.eventDate ? new Date(event.eventDate) : new Date(),
            title: event.title || '',
            description: event.description || '',
            outcome: event.outcome || '',
            status: event.status || 'Scheduled',
            visibleToEmployee: event.visibleToEmployee || false
          });
          setExistingFollowUps(event.followUps || []);
        } finally {
          setLoadingDetails(false);
        }
      } else if (!event && open) {
        // New event
        setForm({ eventTypeId: '', eventDate: new Date(), title: '', description: '', outcome: '', status: 'Scheduled', visibleToEmployee: false });
        setExistingFollowUps([]);
      }
      setShowFollowUpForm(false);
      setEditingFollowUpId(null);
      setNewFollowUp({ description: '', dueDate: null, assignedToUserId: null, additionalRecipientUserIds: [], reminderStartDaysBefore: 14, reminderFrequencyDays: 7, status: 'Pending' });
      setSelectedUser(null);
      setSelectedAdditionalUsers([]);
      setError(null);
    };

    loadEventDetails();
  }, [event, open]);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleDateChange = (date) => {
    setForm(prev => ({ ...prev, eventDate: date }));
  };

  const handleEventTypeChange = (e) => {
    const typeId = e.target.value;
    const type = eventTypes.find(t => t.id === typeId);
    setForm(prev => ({
      ...prev,
      eventTypeId: typeId,
      title: prev.title || type?.name || ''
    }));
    // Pre-fill follow-up form if type has default
    if (type?.defaultFollowUpDescription && !showFollowUpForm) {
      setShowFollowUpForm(true);
      const dueDate = type.defaultFollowUpDays
        ? new Date(Date.now() + type.defaultFollowUpDays * 24 * 60 * 60 * 1000)
        : null;
      setNewFollowUp({ description: type.defaultFollowUpDescription, dueDate, assignedToUserId: null, additionalRecipientUserIds: [], reminderStartDaysBefore: 14, reminderFrequencyDays: 7, status: 'Pending' });
    }
  };

  const handleAdditionalUsersChange = (evt, newValue) => {
    setSelectedAdditionalUsers(newValue);
    setNewFollowUp(prev => ({ ...prev, additionalRecipientUserIds: newValue.map(u => u.id) }));
  };

  const handleNewFollowUpChange = (field) => (e) => {
    setNewFollowUp(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleUserChange = (evt, newValue) => {
    setSelectedUser(newValue);
    setNewFollowUp(prev => ({ ...prev, assignedToUserId: newValue?.id || null }));
  };

  const handleAddFollowUp = async () => {
    if (!newFollowUp.description || !newFollowUp.dueDate) {
      setError(t('careerEvents.errors.requiredFields', 'Please fill in required fields'));
      return;
    }
    setAddingFollowUp(true);
    try {
      const payload = {
        description: newFollowUp.description,
        dueDate: newFollowUp.dueDate.toISOString(),
        assignedToUserId: newFollowUp.assignedToUserId || 1,
        additionalRecipientUserIds: newFollowUp.additionalRecipientUserIds || [],
        reminderStartDaysBefore: newFollowUp.reminderStartDaysBefore,
        reminderFrequencyDays: newFollowUp.reminderFrequencyDays
      };

      if (editingFollowUpId) {
        // Update existing follow-up
        const updated = await careerEventsService.updateFollowUp(editingFollowUpId, payload);
        setExistingFollowUps(prev => prev.map(f => f.id === editingFollowUpId ? updated : f));
      } else {
        // Create new follow-up
        const created = await careerEventsService.createFollowUp(event.id, payload);
        setExistingFollowUps(prev => [created, ...prev]);
      }
      setShowFollowUpForm(false);
      setEditingFollowUpId(null);
      setNewFollowUp({ description: '', dueDate: null, assignedToUserId: null, additionalRecipientUserIds: [], reminderStartDaysBefore: 14, reminderFrequencyDays: 7, status: 'Pending' });
      setSelectedUser(null);
      setSelectedAdditionalUsers([]);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save follow-up');
    } finally {
      setAddingFollowUp(false);
    }
  };

  // Handle accordion expand/collapse - loads data for editing when expanded
  const handleAccordionChange = (followUp) => (event, isExpanded) => {
    if (isExpanded) {
      // Close the "add new" form when opening an accordion for editing
      setShowFollowUpForm(false);
      setExpandedFollowUpId(followUp.id);
      setEditingFollowUpId(followUp.id);
      setNewFollowUp({
        description: followUp.description,
        dueDate: new Date(followUp.dueDate),
        assignedToUserId: followUp.assignedToUserId,
        additionalRecipientUserIds: followUp.additionalRecipientUserIds || [],
        reminderStartDaysBefore: followUp.reminderStartDaysBefore || 14,
        reminderFrequencyDays: followUp.reminderFrequencyDays || 7,
        status: followUp.status === 'Overdue' ? 'Pending' : (followUp.status || 'Pending')
      });
      // Set selected user for display
      if (followUp.assignedToUserId && followUp.assignedToUserName) {
        setSelectedUser({ id: followUp.assignedToUserId, displayName: followUp.assignedToUserName });
      } else {
        setSelectedUser(null);
      }
      // Set additional recipients for display
      if (followUp.additionalRecipients?.length > 0) {
        setSelectedAdditionalUsers(followUp.additionalRecipients.map(r => ({ id: r.id, displayName: r.displayName })));
      } else {
        setSelectedAdditionalUsers([]);
      }
    } else {
      setExpandedFollowUpId(null);
      setEditingFollowUpId(null);
      setNewFollowUp({ description: '', dueDate: null, assignedToUserId: null, additionalRecipientUserIds: [], reminderStartDaysBefore: 14, reminderFrequencyDays: 7, status: 'Pending' });
      setSelectedUser(null);
      setSelectedAdditionalUsers([]);
    }
  };

  const handleCancelFollowUpEdit = () => {
    setExpandedFollowUpId(null);
    setShowFollowUpForm(false);
    setEditingFollowUpId(null);
    setNewFollowUp({ description: '', dueDate: null, assignedToUserId: null, additionalRecipientUserIds: [], reminderStartDaysBefore: 14, reminderFrequencyDays: 7, status: 'Pending' });
    setSelectedUser(null);
    setSelectedAdditionalUsers([]);
  };

  const handleDeleteFollowUp = async (followUpId) => {
    setDeletingId(followUpId);
    try {
      await careerEventsService.deleteFollowUp(followUpId);
      setExistingFollowUps(prev => prev.filter(f => f.id !== followUpId));
    } catch (err) {
      console.error('Failed to delete follow-up:', err);
    } finally {
      setDeletingId(null);
    }
  };

  // Inline status change handler
  const handleInlineStatusChange = async (followUpId, newStatus) => {
    try {
      await careerEventsService.updateFollowUpStatus(followUpId, newStatus);
      setExistingFollowUps(prev => prev.map(f =>
        f.id === followUpId
          ? { ...f, status: newStatus, completedAt: (newStatus === 'Completed' || newStatus === 'Cancelled') ? new Date().toISOString() : f.completedAt }
          : f
      ));
    } catch (err) {
      console.error('Failed to update follow-up status:', err);
    }
  };

  const openReminderDialog = (followUp) => {
    setReminderFollowUp(followUp);
    // Pre-populate with assigned user if exists
    const assigneeEmail = followUp.assignedToUserEmail || '';
    setReminderRecipients(assigneeEmail ? [assigneeEmail] : []);
    setReminderUpdateTracking(false);
    setReminderResult(null);
    setReminderDialogOpen(true);
  };

  const closeReminderDialog = () => {
    setReminderDialogOpen(false);
    setReminderFollowUp(null);
    setReminderRecipients([]);
    setReminderResult(null);
  };

  const handleSendReminder = async () => {
    if (!reminderFollowUp) return;
    if (reminderRecipients.length === 0) {
      setReminderResult({ error: 'Please add at least one recipient' });
      return;
    }

    setReminderSending(true);
    setReminderResult(null);
    try {
      const result = await careerEventsService.sendReminderNow(
        reminderFollowUp.id,
        reminderRecipients,
        reminderUpdateTracking
      );

      // Update the follow-up in state if tracking was updated
      if (result.trackingUpdated) {
        setExistingFollowUps(prev => prev.map(f =>
          f.id === reminderFollowUp.id
            ? { ...f, lastReminderSentAt: new Date().toISOString(), reminderCount: result.reminderCount }
            : f
        ));
      }

      setReminderResult({
        success: true,
        sentTo: result.sentTo,
        failed: result.failed,
        eventType: result.eventType
      });
    } catch (err) {
      console.error('Failed to send reminder:', err);
      setReminderResult({ error: err.response?.data?.error || 'Failed to send reminder' });
    } finally {
      setReminderSending(false);
    }
  };

  const handleAddRecipient = (email) => {
    if (email && !reminderRecipients.includes(email)) {
      setReminderRecipients(prev => [...prev, email]);
    }
  };

  const handleRemoveRecipient = (email) => {
    setReminderRecipients(prev => prev.filter(e => e !== email));
  };

  const handleSave = async () => {
    if (!form.eventTypeId || !form.title) {
      setError(t('careerEvents.errors.requiredFields', 'Please fill in required fields'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await careerEventsService.updateEvent(event.id, {
          eventDate: form.eventDate.toISOString(),
          title: form.title,
          description: form.description,
          outcome: form.outcome,
          status: form.status,
          visibleToEmployee: form.visibleToEmployee
        });
      } else {
        // Step 1: Create the Career Event
        const payload = {
          employeeId,
          eventTypeId: form.eventTypeId,
          eventDate: form.eventDate.toISOString(),
          title: form.title,
          description: form.description,
          status: form.status,
          visibleToEmployee: form.visibleToEmployee
        };
        const createdEvent = await careerEventsService.createEvent(payload);

        // Step 2: If follow-up form is filled, create follow-up via dedicated endpoint
        if (showFollowUpForm && newFollowUp.description && newFollowUp.dueDate) {
          const followUpPayload = {
            description: newFollowUp.description,
            dueDate: newFollowUp.dueDate.toISOString(),
            assignedToUserId: newFollowUp.assignedToUserId || 1,
            additionalRecipientUserIds: newFollowUp.additionalRecipientUserIds || [],
            reminderStartDaysBefore: newFollowUp.reminderStartDaysBefore || 14,
            reminderFrequencyDays: newFollowUp.reminderFrequencyDays || 7
          };
          await careerEventsService.createFollowUp(createdEvent.id, followUpPayload);
        }
      }
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.message || t('careerEvents.errors.saveFailed', 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const dialogTitle = viewMode
    ? t('careerEvents.dialog.viewTitle', 'View Event')
    : isEdit
      ? t('careerEvents.dialog.editTitle', 'Edit Event')
      : t('careerEvents.dialog.addTitle', 'New Career Event');

  return (
    <>
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {personName && <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>{personName}</Typography>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loadingDetails ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth disabled={viewMode || isEdit}>
              <InputLabel>{t('careerEvents.fields.eventType', 'Event Type')}</InputLabel>
              <Select value={form.eventTypeId} onChange={handleEventTypeChange} label={t('careerEvents.fields.eventType', 'Event Type')}>
                {eventTypes.map(et => (
                  <MenuItem key={et.id} value={et.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18, color: et.color }}>{et.icon || 'event'}</span>
                      {t(`careerEvents.types.${et.code}`, et.name)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker label={t('careerEvents.fields.date', 'Date')} value={form.eventDate} onChange={handleDateChange} disabled={viewMode}
              slotProps={{ textField: { fullWidth: true } }} />
            <TextField label={t('careerEvents.fields.title', 'Title')} value={form.title} onChange={handleChange('title')} fullWidth disabled={viewMode} required />
            <TextField label={t('careerEvents.fields.description', 'Description')} value={form.description} onChange={handleChange('description')} fullWidth multiline rows={3} disabled={viewMode} />
            <FormControl fullWidth>
              <InputLabel>{t('careerEvents.fields.status', 'Status')}</InputLabel>
              <Select value={form.status} onChange={handleChange('status')} label={t('careerEvents.fields.status', 'Status')} disabled={viewMode}>
                {statusOptions.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </Select>
            </FormControl>
            {(isEdit || viewMode) && (
              <TextField label={t('careerEvents.fields.outcome', 'Outcome')} value={form.outcome} onChange={handleChange('outcome')} fullWidth multiline rows={2} disabled={viewMode} />
            )}

            {/* Visibility checkbox */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.visibleToEmployee}
                  onChange={(e) => setForm(prev => ({ ...prev, visibleToEmployee: e.target.checked }))}
                  disabled={viewMode}
                />
              }
              label={t('careerEvents.fields.visibleToEmployee', 'Visible to Employee')}
            />

            {/* Follow-ups Section - always visible */}
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" color="primary">{t('careerEvents.followUp.title', 'Follow-up Tasks')}</Typography>
                {existingFollowUps.length > 0 && (
                  <Chip size="small" label={existingFollowUps.length} color="primary" />
                )}
              </Box>
              <Box>
                {existingFollowUps.length > 0 && (
                  <IconButton size="small" onClick={() => setFollowUpsExpanded(!followUpsExpanded)}>
                    {followUpsExpanded ? <CollapseIcon /> : <ExpandIcon />}
                  </IconButton>
                )}
                {!viewMode && isEdit && !showFollowUpForm && (
                  <Tooltip title={t('careerEvents.followUp.addNew', 'Add Follow-up')}>
                    <IconButton size="small" color="primary" onClick={() => setShowFollowUpForm(true)}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            {/* Existing follow-ups list - Accordion style */}
            <Collapse in={followUpsExpanded && existingFollowUps.length > 0}>
              <Box sx={{ mt: 1 }}>
                {existingFollowUps.map(fu => {
                  const statusColor = fu.status === 'Completed' ? 'success' : fu.status === 'Cancelled' ? 'default' : fu.status === 'Overdue' ? 'error' : 'warning';
                  const borderColor = fu.status === 'Completed' ? 'success.main' : fu.status === 'Cancelled' ? 'grey.400' : fu.status === 'Overdue' ? 'error.main' : 'warning.main';
                  const isExpanded = expandedFollowUpId === fu.id;

                  return (
                    <Accordion
                      key={fu.id}
                      expanded={isExpanded}
                      onChange={viewMode ? undefined : handleAccordionChange(fu)}
                      sx={{
                        mb: 1,
                        borderLeft: '4px solid',
                        borderLeftColor: borderColor,
                        '&:before': { display: 'none' },
                        bgcolor: fu.status === 'Overdue' ? 'error.lighter' : 'background.paper'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={viewMode ? null : <ExpandMoreIcon />}
                        sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{fu.description}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                            <Chip
                              size="small"
                              label={t(`careerEvents.followUp.statusValues.${fu.status?.toLowerCase()}`, fu.status)}
                              color={statusColor}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {fu.dueDate ? new Date(fu.dueDate).toLocaleDateString() : ''}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        {viewMode ? (
                          /* View mode - just show details */
                          <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('careerEvents.followUp.assignee', 'Assigned to')}: {fu.assignedToUserName || 'N/A'}
                            </Typography>
                            {fu.lastReminderSentAt && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                📧 {t('careerEvents.followUp.lastReminder', 'Last reminder')}: {new Date(fu.lastReminderSentAt).toLocaleString()}
                                {fu.reminderCount > 0 && ` (${fu.reminderCount} ${t('careerEvents.followUp.sent', 'sent')})`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          /* Edit mode - show editable fields */
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              label={t('careerEvents.followUp.description', 'Description')}
                              value={newFollowUp.description}
                              onChange={handleNewFollowUpChange('description')}
                              fullWidth
                              multiline
                              rows={2}
                              size="small"
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <DatePicker
                                label={t('careerEvents.followUp.dueDate', 'Due Date')}
                                value={newFollowUp.dueDate}
                                onChange={(date) => setNewFollowUp(prev => ({ ...prev, dueDate: date }))}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                              />
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel>{t('careerEvents.followUp.status', 'Status')}</InputLabel>
                                <Select
                                  value={newFollowUp.status}
                                  onChange={handleNewFollowUpChange('status')}
                                  label={t('careerEvents.followUp.status', 'Status')}
                                >
                                  {followUpStatusOptions.map(s => (
                                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            <Autocomplete
                              options={users}
                              value={selectedUser}
                              onChange={handleUserChange}
                              onInputChange={(e, value) => searchUsers(value)}
                              getOptionLabel={(option) => option.displayName || option.email || ''}
                              isOptionEqualToValue={(option, value) => option.id === value?.id}
                              loading={usersLoading}
                              size="small"
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={t('careerEvents.followUp.assignee', 'Assigned To')}
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <FormControl size="small" fullWidth>
                                <InputLabel>{t('careerEvents.followUp.reminderStartBefore', 'Remind Before')}</InputLabel>
                                <Select
                                  value={newFollowUp.reminderStartDaysBefore}
                                  onChange={handleNewFollowUpChange('reminderStartDaysBefore')}
                                  label={t('careerEvents.followUp.reminderStartBefore', 'Remind Before')}
                                >
                                  <MenuItem value={7}>1 week</MenuItem>
                                  <MenuItem value={14}>2 weeks</MenuItem>
                                  <MenuItem value={30}>1 month</MenuItem>
                                </Select>
                              </FormControl>
                              <FormControl size="small" fullWidth>
                                <InputLabel>{t('careerEvents.followUp.reminderFrequency', 'Repeat')}</InputLabel>
                                <Select
                                  value={newFollowUp.reminderFrequencyDays}
                                  onChange={handleNewFollowUpChange('reminderFrequencyDays')}
                                  label={t('careerEvents.followUp.reminderFrequency', 'Repeat')}
                                >
                                  <MenuItem value={0}>No repeat</MenuItem>
                                  <MenuItem value={7}>Weekly</MenuItem>
                                  <MenuItem value={14}>Bi-weekly</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            {fu.lastReminderSentAt && (
                              <Typography variant="caption" color="text.secondary">
                                📧 {t('careerEvents.followUp.lastReminder', 'Last reminder')}: {new Date(fu.lastReminderSentAt).toLocaleString()}
                                {fu.reminderCount > 0 && ` (${fu.reminderCount} ${t('careerEvents.followUp.sent', 'sent')})`}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title={t('careerEvents.followUp.sendReminderNow', 'Send Reminder')}>
                                  <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openReminderDialog(fu); }}>
                                    <SendReminderIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={t('common.delete', 'Delete')}>
                                  <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteFollowUp(fu.id); }} disabled={deletingId === fu.id}>
                                    {deletingId === fu.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                  </IconButton>
                                </Tooltip>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" onClick={handleCancelFollowUpEdit}>{t('common.cancel', 'Cancel')}</Button>
                                <Button size="small" variant="contained" onClick={handleAddFollowUp} disabled={addingFollowUp}>
                                  {addingFollowUp ? <CircularProgress size={16} /> : t('common.save', 'Save')}
                                </Button>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            </Collapse>

            {/* Add new follow-up form - for new events or when adding to existing */}
            {/* Hide when an accordion is expanded for editing */}
            {!viewMode && !expandedFollowUpId && (showFollowUpForm || !isEdit) && (
              <Collapse in={showFollowUpForm || !isEdit}>
                {!isEdit && !showFollowUpForm && (
                  <Button startIcon={<AddIcon />} onClick={() => setShowFollowUpForm(true)} sx={{ mt: 1 }}>
                    {t('careerEvents.followUp.addNew', 'Add Follow-up')}
                  </Button>
                )}
                {showFollowUpForm && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main', mt: 1 }}>
                    <TextField
                      label={t('careerEvents.followUp.description', 'Follow-up Description')}
                      value={newFollowUp.description}
                      onChange={handleNewFollowUpChange('description')}
                      fullWidth
                      multiline
                      rows={2}
                    />
                    <DatePicker
                      label={t('careerEvents.followUp.dueDate', 'Due Date')}
                      value={newFollowUp.dueDate}
                      onChange={(date) => setNewFollowUp(prev => ({ ...prev, dueDate: date }))}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                    <Autocomplete
                      options={users}
                      value={selectedUser}
                      onChange={handleUserChange}
                      onInputChange={(e, value) => searchUsers(value)}
                      getOptionLabel={(option) => option.displayName || option.email || ''}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      loading={usersLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('careerEvents.followUp.assignee', 'Assigned To')}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <Autocomplete
                      multiple
                      options={users}
                      value={selectedAdditionalUsers}
                      onChange={handleAdditionalUsersChange}
                      onInputChange={(e, value) => searchUsers(value)}
                      getOptionLabel={(option) => option.displayName || option.email || ''}
                      isOptionEqualToValue={(option, value) => option.id === value?.id}
                      loading={usersLoading}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip
                            label={option.displayName || option.email}
                            size="small"
                            {...getTagProps({ index })}
                          />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('careerEvents.followUp.additionalRecipients', 'Additional Recipients')}
                          placeholder={t('careerEvents.followUp.searchUsers', 'Search users...')}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <FormControl fullWidth>
                      <InputLabel>{t('careerEvents.followUp.reminderStartBefore', 'Start Reminders Before')}</InputLabel>
                      <Select
                        value={newFollowUp.reminderStartDaysBefore}
                        onChange={handleNewFollowUpChange('reminderStartDaysBefore')}
                        label={t('careerEvents.followUp.reminderStartBefore', 'Start Reminders Before')}
                      >
                        <MenuItem value={7}>{t('careerEvents.followUp.oneWeekBefore', '1 week before')}</MenuItem>
                        <MenuItem value={14}>{t('careerEvents.followUp.twoWeeksBefore', '2 weeks before')}</MenuItem>
                        <MenuItem value={30}>{t('careerEvents.followUp.oneMonthBefore', '1 month before')}</MenuItem>
                        <MenuItem value={60}>{t('careerEvents.followUp.twoMonthsBefore', '2 months before')}</MenuItem>
                        <MenuItem value={90}>{t('careerEvents.followUp.threeMonthsBefore', '3 months before')}</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>{t('careerEvents.followUp.reminderFrequency', 'Repeat Every')}</InputLabel>
                      <Select
                        value={newFollowUp.reminderFrequencyDays}
                        onChange={handleNewFollowUpChange('reminderFrequencyDays')}
                        label={t('careerEvents.followUp.reminderFrequency', 'Repeat Every')}
                      >
                        <MenuItem value={0}>{t('careerEvents.followUp.noReminder', 'No repeat')}</MenuItem>
                        <MenuItem value={1}>{t('careerEvents.followUp.daily', 'Daily')}</MenuItem>
                        <MenuItem value={7}>{t('careerEvents.followUp.weekly', 'Weekly')}</MenuItem>
                        <MenuItem value={14}>{t('careerEvents.followUp.biweekly', 'Every 2 weeks')}</MenuItem>
                        <MenuItem value={30}>{t('careerEvents.followUp.monthly', 'Monthly')}</MenuItem>
                      </Select>
                    </FormControl>
                    {editingFollowUpId && (
                      <FormControl fullWidth>
                        <InputLabel>{t('careerEvents.followUp.status', 'Status')}</InputLabel>
                        <Select
                          value={newFollowUp.status}
                          onChange={handleNewFollowUpChange('status')}
                          label={t('careerEvents.followUp.status', 'Status')}
                        >
                          {followUpStatusOptions.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    )}
                    {isEdit && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button size="small" onClick={handleCancelFollowUpEdit}>{t('common.cancel', 'Cancel')}</Button>
                        <Button size="small" variant="contained" onClick={handleAddFollowUp} disabled={addingFollowUp}>
                          {addingFollowUp ? <CircularProgress size={16} /> : (editingFollowUpId ? t('common.save', 'Save') : t('common.add', 'Add'))}
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Collapse>
            )}
          </Box>
        </LocalizationProvider>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>{t('common.close', 'Close')}</Button>
        {!viewMode && <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? t('common.saving', 'Saving...') : t('common.save', 'Save')}</Button>}
      </DialogActions>
    </Dialog>

    {/* Send Reminder Dialog */}
    <Dialog open={reminderDialogOpen} onClose={closeReminderDialog} maxWidth="sm" fullWidth>
      <DialogTitle>{t('careerEvents.followUp.sendReminderTitle', 'Send Reminder Notification')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {reminderFollowUp && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2">
                <strong>{t('careerEvents.followUp.description', 'Follow-up')}:</strong> {reminderFollowUp.description}
              </Typography>
              <Typography variant="caption" display="block">
                {t('careerEvents.followUp.dueDate', 'Due')}: {reminderFollowUp.dueDate ? new Date(reminderFollowUp.dueDate).toLocaleDateString() : 'N/A'}
              </Typography>
            </Alert>
          )}

          <Typography variant="subtitle2">{t('careerEvents.followUp.recipients', 'Recipients')}</Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {reminderRecipients.map(email => (
              <Chip
                key={email}
                label={email}
                onDelete={() => handleRemoveRecipient(email)}
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            {reminderRecipients.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('careerEvents.followUp.noRecipients', 'No recipients added')}
              </Typography>
            )}
          </Box>

          <Autocomplete
            options={users}
            getOptionLabel={(option) => `${option.displayName || option.email} (${option.email})`}
            onChange={(e, newValue) => {
              if (newValue?.email) {
                handleAddRecipient(newValue.email);
              }
            }}
            onInputChange={(e, value) => {
              if (value.length >= 2) searchUsers(value);
            }}
            loading={usersLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('careerEvents.followUp.addRecipient', 'Add recipient')}
                placeholder={t('careerEvents.followUp.searchUsers', 'Search users...')}
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label={t('careerEvents.followUp.customEmail', 'Or enter email manually')}
            size="small"
            placeholder="email@example.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const email = e.target.value.trim();
                if (email && email.includes('@')) {
                  handleAddRecipient(email);
                  e.target.value = '';
                }
              }
            }}
            helperText={t('careerEvents.followUp.pressEnter', 'Press Enter to add')}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={reminderUpdateTracking}
                onChange={(e) => setReminderUpdateTracking(e.target.checked)}
              />
            }
            label={t('careerEvents.followUp.updateTracking', 'Update reminder tracking (count as official reminder)')}
          />

          {reminderResult && (
            <Alert severity={reminderResult.error ? 'error' : 'success'}>
              {reminderResult.error ? (
                reminderResult.error
              ) : (
                <>
                  <Typography variant="body2">
                    ✅ {t('careerEvents.followUp.sentTo', 'Sent to')}: {reminderResult.sentTo?.join(', ')}
                  </Typography>
                  {reminderResult.failed?.length > 0 && (
                    <Typography variant="body2" color="error">
                      ❌ {t('careerEvents.followUp.failedTo', 'Failed')}: {reminderResult.failed?.join(', ')}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block">
                    {t('careerEvents.followUp.eventType', 'Template used')}: {reminderResult.eventType}
                  </Typography>
                </>
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeReminderDialog}>{t('common.close', 'Close')}</Button>
        <Button
          variant="contained"
          onClick={handleSendReminder}
          disabled={reminderSending || reminderRecipients.length === 0}
          startIcon={reminderSending ? <CircularProgress size={16} /> : <SendReminderIcon />}
        >
          {reminderSending ? t('common.sending', 'Sending...') : t('careerEvents.followUp.send', 'Send')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

